import {FC, ReactElement} from 'react'
import {DndProvider, useDrag, useDrop} from 'react-dnd'
import {HTML5Backend} from 'react-dnd-html5-backend'
import {makeStyles} from 'tss-react/mui'


const DraggableList: FC<DraggableListProps> = ({
  items = [], listItemsClassName, onDisplayItem, onItemsSwapped,
}) => {
  const {classes: styles} = useStyles({})
  const swapItems = (from: number, to: number) => {
    const updatedItems = [...items];
    [updatedItems[from], updatedItems[to]] = [updatedItems[to], updatedItems[from]]
    onItemsSwapped?.(updatedItems, from, to)
  }
  return (
    <DndProvider backend={HTML5Backend}>
      <ul className={styles.root}>
        {items?.map((item, index) => {
          const child = onDisplayItem?.(item, index) ?? null
          if (child === null) return null
          return (
            <DraggableItem
              className={listItemsClassName}
              key={index}
              index={index}
              swapItems={swapItems}
            >
              {child}
            </DraggableItem>
          )
        })}
      </ul>
    </DndProvider>
  )
}

const DraggableItem: FC<DraggableItemProps> = ({
  children, className, index, swapItems, ...props
}) => {
  const [{isDragging}, drag] = useDrag({
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    item: {index},
    type: "ITEM",
  })
  const [, drop] = useDrop({
    accept: "ITEM",
    drop: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        swapItems(draggedItem.index, index)
      }
    },
  })
  const {classes: styles} = useStyles({isDragging})
  return (
    <li
      ref={node => drag(drop(node))}
      {...props}
      className={`DraggableListItem ${styles.listItem} ${className ?? ''}`}
    >
      {children}
    </li>
  )
}

const useStyles = makeStyles<MakeStyleProps>()((_, {isDragging}) => ({
  listItem: {
    cursor: "grab",
    opacity: isDragging ? 0.5 : 1,
    width: '100%',
  },
  root: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
}))

interface DraggableListProps {
  items: any[]
  listItemsClassName?: string
  onDisplayItem?: (item: any, index: number) => ReactElement | null
  onItemsSwapped?: (updatedItems: any[], from: number, to: number) => void
}

interface DraggableItemProps {
  children: ReactElement<{listItemsClassName?: string}> | null
  className?: string
  index: number
  swapItems: (from: number, to: number) => void
}

interface MakeStyleProps {
  isDragging?: boolean
}

export {DraggableItem, DraggableList}
